import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, BACKEND_URL } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowUp, ArrowDown, Plus, Pencil, Trash2, Image as ImageIcon, X, Star, RotateCcw, Search,
} from 'lucide-react';

const emptyForm = {
  title: '',
  description: '',
  category: '',
  date: '',
  reading_time: '',
  path: '',
  image_path: null,
};

const AdminFeaturedArticlesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reorderingId, setReorderingId] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getFeaturedArticlesAdmin();
      setItems(data);
    } catch (err) {
      console.error('Error fetching featured articles:', err);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreateDialog = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.featured_article_id);
    setFormData({
      title: item.title,
      description: item.description,
      category: item.category || '',
      date: item.date || '',
      reading_time: item.reading_time || '',
      path: item.path,
      image_path: item.image_path || null,
    });
    setDialogOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const result = await adminApi.uploadImage(file, 'featured');
      setFormData((prev) => ({ ...prev, image_path: result.path }));
    } catch (err) {
      console.error('Error uploading image:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image_path: null }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return false;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return false;
    }
    if (!formData.path.trim()) {
      toast.error('Link path is required (e.g. /articles/bms)');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category.trim() || null,
        date: formData.date.trim() || null,
        reading_time: formData.reading_time.trim() || null,
        path: formData.path.trim(),
        image_path: formData.image_path || null,
      };

      if (editingId) {
        const updated = await adminApi.updateFeaturedArticle(editingId, payload);
        setItems((prev) => prev.map((it) => (it.featured_article_id === editingId ? updated : it)));
        toast.success('Article updated');
      } else {
        const created = await adminApi.createFeaturedArticle(payload);
        setItems((prev) => [...prev, created]);
        toast.success('Article created');
      }
      setDialogOpen(false);
    } catch (err) {
      console.error('Error saving featured article:', err);
      toast.error(err.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await adminApi.deleteFeaturedArticle(deleteId);
      setItems((prev) => prev.map((it) => (
        it.featured_article_id === deleteId ? { ...it, is_deleted: true } : it
      )));
      toast.success('Article moved to Deleted');
    } catch (err) {
      console.error('Error deleting featured article:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete article');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleRestore = async (item) => {
    setRestoringId(item.featured_article_id);
    try {
      await adminApi.restoreFeaturedArticle(item.featured_article_id);
      setItems((prev) => prev.map((it) => (
        it.featured_article_id === item.featured_article_id ? { ...it, is_deleted: false } : it
      )));
      toast.success('Article restored');
    } catch (err) {
      console.error('Error restoring featured article:', err);
      toast.error(err.response?.data?.detail || 'Failed to restore article');
    } finally {
      setRestoringId(null);
    }
  };

  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    setHardDeleting(true);
    try {
      await adminApi.hardDeleteFeaturedArticle(hardDeleteTarget.featured_article_id);
      setItems((prev) => prev.filter((it) => it.featured_article_id !== hardDeleteTarget.featured_article_id));
      toast.success('Article permanently deleted');
    } catch (err) {
      console.error('Error permanently deleting featured article:', err);
      toast.error(err.response?.data?.detail || 'Failed to permanently delete article');
    } finally {
      setHardDeleting(false);
      setHardDeleteTarget(null);
    }
  };

  const activeItems = items.filter((it) => !it.is_deleted);
  const deletedItems = items.filter((it) => it.is_deleted);

  const matchesSearch = (it) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return [it.title, it.description, it.category, it.path]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(q));
  };

  // Reordering keeps operating on the full, unfiltered activeItems array (see
  // handleMove below) — the move buttons are disabled whenever a search is active
  // so a filtered index is never passed to it.
  const displayedActiveItems = activeItems.filter(matchesSearch);
  const displayedDeletedItems = deletedItems.filter(matchesSearch);
  const isSearching = searchQuery.trim() !== '';

  const handleMove = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= activeItems.length) return;

    const reorderedActive = [...activeItems];
    [reorderedActive[index], reorderedActive[newIndex]] = [reorderedActive[newIndex], reorderedActive[index]];
    setItems([...reorderedActive, ...deletedItems]);

    const movedId = reorderedActive[newIndex].featured_article_id;
    setReorderingId(movedId);
    try {
      await adminApi.reorderFeaturedArticles(
        reorderedActive.map((it, i) => ({ featured_article_id: it.featured_article_id, order: i }))
      );
    } catch (err) {
      console.error('Error reordering featured articles:', err);
      toast.error('Failed to save new order');
      fetchItems();
    } finally {
      setReorderingId(null);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-featured-articles-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon" data-testid="back-to-admin-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight">Articles</h1>
            <p className="text-muted-foreground text-sm">
              Article cards on the public Articles page, grouped into sections by their Section field
            </p>
          </div>
          <Button
            onClick={openCreateDialog}
            className="font-bold uppercase tracking-wider gap-2"
            data-testid="add-featured-article-btn"
          >
            <Plus className="h-4 w-4" />
            Add Card
          </Button>
        </div>

        {!loading && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, section, or link path…"
              className="pl-9"
              data-testid="fa-search-input"
            />
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-2">
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-20 mb-3" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6 bg-white border-2">
              <TabsTrigger value="active" className="font-bold uppercase tracking-wider" data-testid="fa-tab-active">
                Active ({activeItems.length})
              </TabsTrigger>
              <TabsTrigger value="deleted" className="font-bold uppercase tracking-wider" data-testid="fa-tab-deleted">
                Deleted ({deletedItems.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active">
              {displayedActiveItems.length === 0 ? (
                <Card className="border-2 border-dashed">
                  <CardContent className="py-12 text-center">
                    <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {isSearching ? 'No articles match your search.' : 'No articles yet.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {displayedActiveItems.map((item, index) => (
                    <Card
                      key={item.featured_article_id}
                      className="border-2 technical-card"
                      data-testid={`featured-article-row-${item.featured_article_id}`}
                    >
                      <CardContent className="p-4 flex items-start gap-4">
                        <div className="flex flex-col gap-1 pt-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            disabled={isSearching || index === 0 || reorderingId === item.featured_article_id}
                            onClick={() => handleMove(index, 'up')}
                            aria-label="Move up"
                            data-testid={`move-up-${item.featured_article_id}`}
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            disabled={isSearching || index === displayedActiveItems.length - 1 || reorderingId === item.featured_article_id}
                            onClick={() => handleMove(index, 'down')}
                            aria-label="Move down"
                            data-testid={`move-down-${item.featured_article_id}`}
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                        </div>

                        {item.image_path ? (
                          <img
                            src={`${BACKEND_URL}${item.image_path}`}
                            alt=""
                            className="h-20 w-28 object-cover rounded-sm border border-structure shrink-0"
                          />
                        ) : (
                          <div className="h-20 w-28 rounded-sm border border-dashed border-structure flex items-center justify-center shrink-0 text-muted-foreground">
                            <ImageIcon className="h-6 w-6" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          {item.category && (
                            <span className="font-mono text-xs uppercase tracking-wider text-autoxium-blue">
                              {item.category}
                            </span>
                          )}
                          <h3 className="font-chivo text-base font-bold mb-1 truncate">{item.title}</h3>
                          <p className="text-muted-foreground text-sm line-clamp-2 mb-1">{item.description}</p>
                          <p className="text-xs text-muted-foreground font-mono truncate">{item.path}</p>
                        </div>

                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => openEditDialog(item)}
                            aria-label="Edit"
                            data-testid={`edit-featured-article-${item.featured_article_id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteId(item.featured_article_id)}
                            aria-label="Delete"
                            data-testid={`delete-featured-article-${item.featured_article_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="deleted">
              {displayedDeletedItems.length === 0 ? (
                <Card className="border-2 border-dashed">
                  <CardContent className="py-12 text-center">
                    <Trash2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {isSearching ? 'No deleted articles match your search.' : 'No deleted articles.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {displayedDeletedItems.map((item) => (
                    <Card
                      key={item.featured_article_id}
                      className="border-2 technical-card"
                      data-testid={`featured-article-deleted-row-${item.featured_article_id}`}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        {item.image_path ? (
                          <img
                            src={`${BACKEND_URL}${item.image_path}`}
                            alt=""
                            className="h-20 w-28 object-cover rounded-sm border border-structure shrink-0"
                          />
                        ) : (
                          <div className="h-20 w-28 rounded-sm border border-dashed border-structure flex items-center justify-center shrink-0 text-muted-foreground">
                            <ImageIcon className="h-6 w-6" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          {item.category && (
                            <span className="font-mono text-xs uppercase tracking-wider text-autoxium-blue">
                              {item.category}
                            </span>
                          )}
                          <h3 className="font-chivo text-base font-bold mb-1 truncate">{item.title}</h3>
                          <p className="text-muted-foreground text-sm line-clamp-2">{item.description}</p>
                        </div>

                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={restoringId === item.featured_article_id}
                            onClick={() => handleRestore(item)}
                            data-testid={`restore-featured-article-${item.featured_article_id}`}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            {restoringId === item.featured_article_id ? 'Restoring...' : 'Restore'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-destructive text-destructive hover:bg-destructive hover:text-white"
                            onClick={() => setHardDeleteTarget(item)}
                            data-testid={`hard-delete-featured-article-${item.featured_article_id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Permanently
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-chivo uppercase">
              {editingId ? 'Edit Article' : 'Add Article'}
            </DialogTitle>
            <DialogDescription>
              This card appears on the Articles page, under its section.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="fa-title">Title *</Label>
              <Input
                id="fa-title"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                data-testid="fa-title-input"
              />
            </div>

            <div>
              <Label htmlFor="fa-description">Description *</Label>
              <Textarea
                id="fa-description"
                rows={3}
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                data-testid="fa-description-input"
              />
            </div>

            <div>
              <Label htmlFor="fa-path">Link Path *</Label>
              <Input
                id="fa-path"
                placeholder="/articles/bms"
                value={formData.path}
                onChange={(e) => handleFieldChange('path', e.target.value)}
                data-testid="fa-path-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Where the card links to, e.g. /articles/bms or /tools/ev-battery-sizing/guide
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fa-category">Section</Label>
                <Input
                  id="fa-category"
                  placeholder="Process"
                  value={formData.category}
                  onChange={(e) => handleFieldChange('category', e.target.value)}
                  data-testid="fa-category-input"
                />
              </div>
              <div>
                <Label htmlFor="fa-date">Date</Label>
                <Input
                  id="fa-date"
                  placeholder="July 2026"
                  value={formData.date}
                  onChange={(e) => handleFieldChange('date', e.target.value)}
                  data-testid="fa-date-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="fa-reading-time">Reading Time</Label>
              <Input
                id="fa-reading-time"
                placeholder="8–10 min read"
                value={formData.reading_time}
                onChange={(e) => handleFieldChange('reading_time', e.target.value)}
                data-testid="fa-reading-time-input"
              />
            </div>

            <div>
              <Label>Image</Label>
              {formData.image_path ? (
                <div className="relative w-fit mt-2">
                  <img
                    src={`${BACKEND_URL}${formData.image_path}`}
                    alt=""
                    className="h-28 w-40 object-cover rounded-sm border border-structure"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1"
                    aria-label="Remove image"
                    data-testid="fa-remove-image-btn"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="fa-image-input"
                    data-testid="fa-image-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingImage}
                    onClick={() => fileInputRef.current?.click()}
                    className="gap-2"
                  >
                    <ImageIcon className="h-4 w-4" />
                    {uploadingImage ? 'Uploading…' : 'Upload Image'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="fa-save-btn">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo uppercase">Delete Article?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the card from the Articles page and moves it to the Deleted tab, where it can be
              restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-featured-article-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hard delete confirmation */}
      <AlertDialog open={!!hardDeleteTarget} onOpenChange={() => setHardDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo uppercase">Permanently Delete Article?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete "{hardDeleteTarget?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleHardDelete}
              disabled={hardDeleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-hard-delete-featured-article-btn"
            >
              {hardDeleting ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminFeaturedArticlesPage;
